import { useCallback, useRef, useState } from 'react'

import { type Flashcard, createCard, fetchCards, fetchTags } from '../../api/flashcards'
import { uploadFile } from '../../api/library'
import { type Linked, type LinkedKind, fetchLinks, link, unlink } from '../../api/links'
import { asFailure, type Failure } from '../../api/request'
import { type FiledKind, fileUnderSubject } from '../../api/subjects'
import { type Task, addTask, fetchTasks, isDone, tickOffTask } from '../../api/tasks'
import { type Appointment, type Writeup, fetchWriteup, writeAboutIt } from '../../api/timetable'
import { useLoad } from '../../api/useLoad'
import { navigate } from '../../frame/route'
import { useAnnounce } from '../../ui/Announce'
import { ContentPicker } from '../../ui/ContentPicker'
import { MARKDOWN } from '../../ui/markup'
import { Button } from '../../ui/Control'
import { RichText } from '../../ui/RichText'
import { CardForm, type CardEntry } from '../cards/CardForm'
import { CardSummary } from '../cards/CardSummary'
import { pathToDocumentWhereverItLies } from '../library/place'
import { pathToAppointmentWheneverItIs } from './place'
import { Done, Failed, Waiting } from '../../ui/Status'
import { useSaving } from '../../ui/useSaving'

/**
 * What happened in this session: the write-up, what belongs to it, and what came out of it.
 *
 * This is the second tab of an appointment, and it is where the learning diary actually is. The whole
 * area is built around one sentence from the issue — *I sit on the sofa with the iPad and have two
 * minutes* — and three decisions follow from it.
 *
 * **The write-up is one field and saves itself.** No title, no form, no save button: the same
 * treatment a note gets, because it is the same act. A session nobody wrote about has none, and that
 * is the ordinary case rather than an empty slot demanding to be filled.
 *
 * **What was difficult is not marked but made into something.** There is no "I did not understand
 * this" tick anywhere here. There is a control that makes a task and one that makes a card, and both
 * of them are ordinary tasks and cards afterwards — so the review before an exam is a list of things
 * to do rather than a text to read again.
 *
 * **Everything that comes into being here inherits the session's subject.** A card made in the
 * Tuesday lecture is filed under that lecture's subject, so it can be found and learned by subject
 * later without anybody having sorted anything.
 */
export interface SessionProps {
  readonly appointment: Appointment
  /** Told when something was written or linked, so the plan can show the new first line. */
  readonly onChanged: () => void
}

export function Session({ appointment, onChanged }: SessionProps) {
  const written = useLoad(
    useCallback((signal: AbortSignal) => fetchWriteup(appointment.id, signal), [appointment.id]),
  )
  const links = useLoad(
    useCallback(
      (signal: AbortSignal) => fetchLinks('APPOINTMENT', appointment.id, signal),
      [appointment.id],
    ),
  )

  return (
    <div className="session">
      {written.load.status === 'waiting' && <Waiting label="Looking up what you wrote." />}
      {written.load.status === 'failed' && (
        <Failed
          failure={written.load.failure}
          onRetry={written.retry}
          title="That did not load"
          hint="Nothing you wrote is lost — it is on the service, and this screen simply could not read it."
        />
      )}

      {written.load.status === 'ready' && (
        <WriteUp
          /*
            Keyed by what the service last said, which is what seeds the editor.

            What is being typed belongs to whoever is typing it, so nothing may write over it — and a
            fresh load is a fresh surface with the new text in it. The same rule the rest of MyLib's
            writing surfaces run on, and the reason there is no effect here writing state.
          */
          key={written.load.value?.updatedAt ?? 'nothing-written'}
          appointmentId={appointment.id}
          writeup={written.load.value}
          onSaved={onChanged}
        />
      )}

      <Belongings
        appointment={appointment}
        links={links.load.status === 'ready' ? links.load.value : []}
        loading={links.load.status === 'waiting'}
        failure={links.load.status === 'failed' ? links.load.failure : null}
        onChanged={() => {
          links.retry()
          onChanged()
        }}
        onRetry={links.retry}
      />
    </div>
  )
}

/**
 * The write-up itself — one field, saved a moment after the typing stops.
 *
 * **Emptying it deletes it**, and there is deliberately no control that says so: there is no such
 * thing as an empty write-up, so emptying one and deleting it are the same gesture. The appointment
 * and everything linked to it are untouched, which is why nothing here asks first.
 */
function WriteUp({
  appointmentId,
  writeup,
  onSaved,
}: {
  readonly appointmentId: string
  readonly writeup: Writeup | null
  readonly onSaved: () => void
}) {
  const [text, setText] = useState(writeup?.text ?? '')
  /**
   * Which markup it is written in.
   *
   * Held here because a write-up that says nothing does not exist: until the first keystroke there is
   * no row to carry a language, so the choice is on the screen and goes with the request that makes
   * one. Once it exists the language is the write-up's own.
   */
  const [format, setFormat] = useState(writeup?.format ?? MARKDOWN)
  const [saved, setSaved] = useState<number | null>(null)
  const [failure, setFailure] = useState<Failure | null>(null)
  /** What the service last confirmed, so an unchanged text is never sent twice. */
  const last = useRef(writeup?.text ?? '')

  const saving = useSaving(async (wanted) => {
    if (wanted === last.current) {
      return
    }
    try {
      await writeAboutIt(appointmentId, wanted, format)
      last.current = wanted
      setSaved(Date.now())
      setFailure(null)
      onSaved()
    } catch (error) {
      // The text stays exactly where it is. Nothing typed is ever thrown away because a request
      // failed — the same promise the notepad makes.
      setFailure(asFailure(error))
    }
  })

  return (
    <section className="session__part">
      <div className="session__head">
        <h3 className="session__title">What happened</h3>
        <p className="session__aside">
          <span>saved as you write</span>
          {saved !== null && <Done key={saved} label="Saved" />}
        </p>
      </div>

      {failure !== null && <Failed failure={failure} />}

      <RichText
        text={text}
        format={format}
        onFormat={async (wanted) => {
          setFormat(wanted)
          // Nothing is sent for a write-up that does not exist yet: writing nothing would be asking
          // for one and deleting it in the same breath. The language goes with the first keystroke.
          if (text.trim() !== '') {
            await writeAboutIt(appointmentId, text, wanted)
            onSaved()
          }
        }}
        label="What happened in this session"
        purpose="writeup"
        /*
          Something already written is opened to be *read* — the drawn text, not the markup it is
          made of — and an empty one opens where the typing goes. On the iPad that is the difference
          between looking something up and having the keyboard jump at you. Either way it is only
          what happens before anybody has chosen; a choice somebody made is remembered.
        */
        initially={writeup === null || writeup.text.trim() === '' ? 'text' : 'preview'}
        placeholder="What came up, and what you did not follow. Two lines are enough."
        onChange={(wanted) => {
          setText(wanted)
          saving.typed(wanted)
        }}
      >
        {/*
          Which markup this is written in, in the surface's head — the same place the pad and the
          library put it, because it is the same question. Switching it changes nothing about the
          text, so there is nothing to ask and nothing to lose.
        */}
      </RichText>
    </section>
  )
}

/**
 * What belongs to this session: the slides, the tasks it produced, the cards made from it.
 *
 * **One list and not two.** Whether something was linked here or made here is a fact about the past
 * that nobody looking for the slides cares about, so it is not a heading — the list is grouped by
 * *kind*, which is what somebody actually scans for.
 */
function Belongings({
  appointment,
  links,
  loading,
  failure,
  onChanged,
  onRetry,
}: {
  readonly appointment: Appointment
  readonly links: readonly Linked[]
  readonly loading: boolean
  readonly failure: Failure | null
  readonly onChanged: () => void
  readonly onRetry: () => void
}) {
  const announce = useAnnounce()
  const [adding, setAdding] = useState<Adding>(null)
  const [working, setWorking] = useState(false)
  const [problem, setProblem] = useState<Failure | null>(null)
  const uploading = useRef<HTMLInputElement>(null)
  /*
    The tags for the cards made here, held above the form rather than in it.

    A lecture yields a series of cards and they are filed alike — so the tags outlive the surface
    closing after each one, and only the two sides are typed afresh. Held here and not saved
    anywhere: they are this sitting's, not the appointment's.
  */
  const [cardTags, setCardTags] = useState<readonly string[]>([])
  /*
    What tags already exist, so a card made here joins them instead of founding a near-duplicate.
    Asked for only once the card form is opened — the first tab must cost nothing.
  */
  const tags = useLoad(
    useCallback(
      async (signal: AbortSignal) => (adding === 'card' ? await fetchTags(signal) : null),
      [adding],
    ),
  )
  const knownTags =
    tags.load.status === 'ready' ? (tags.load.value ?? []).map((tag) => tag.name) : []

  const subjectId = appointment.subject?.id ?? null

  /**
   * Does something, links what came of it to this session, and files it under the session's subject.
   *
   * The three belong together and are one function for that reason: everything made here hangs on the
   * session *and* carries its subject, so that a card made in the Tuesday lecture can be learned by
   * subject months later without anybody having sorted anything.
   */
  const make = async (kind: LinkedKind, id: string, filedAs: FiledKind | null, said: string) => {
    await link({ kind: 'APPOINTMENT', id: appointment.id }, { kind, id })
    if (subjectId !== null && filedAs !== null) {
      // A subject is a second statement and not part of the link, so a failure here must not undo
      // the link: what somebody asked for was that this belongs to the session.
      await fileUnderSubject(subjectId, filedAs, id).catch(() => undefined)
    }
    announce(said)
  }

  /**
   * Does one thing, says what went wrong if it did, and tells the surface around it.
   *
   * `closing` is what tells the two apart: making something is finished by its form going away,
   * while ticking a task off happens *in the list* — and closing a half-typed card because somebody
   * ticked something off beside it would throw away what they were writing.
   */
  const attempt = (work: () => Promise<void>, closing = true) => {
    setWorking(true)
    setProblem(null)
    work()
      .then(() => {
        if (closing) {
          setAdding(null)
        }
        onChanged()
      })
      .catch((error: unknown) => {
        setProblem(asFailure(error))
      })
      .finally(() => {
        setWorking(false)
      })
  }

  const takeAway = (one: Linked) => {
    attempt(async () => {
      await unlink({ kind: 'APPOINTMENT', id: appointment.id }, { kind: one.kind, id: one.id })
      announce('Unlinked. Nothing was deleted.')
    })
  }

  const upload = (files: FileList | null) => {
    const chosen = Array.from(files ?? [])
    if (chosen.length === 0) {
      return
    }
    attempt(async () => {
      for (const file of chosen) {
        // Into the subject's own folder where there is one, and into "not sorted yet" where there is
        // not. Asking which folder at the moment of writing about a lecture would be exactly the form
        // this whole surface exists not to be.
        const taken = await uploadFile(
          file,
          appointment.subject?.rootFolderId ?? null,
          () => undefined,
        )
        await make(
          'DOCUMENT',
          taken.document.id,
          'document',
          `${file.name} is here and belongs to this session.`,
        )
      }
    })
  }

  const grouped = GROUPS.map((group) => ({
    ...group,
    rows: links.filter((one) => one.kind === group.kind),
  })).filter((group) => group.rows.length > 0)

  /*
    The cards themselves, so they can be drawn as cards rather than as a title.

    One request for the deck and not one per card: a session hangs two or three cards off itself and
    a request each would be three round trips to draw one group. It is asked for only when there is a
    card to draw, so a session of documents pays nothing for this.

    **Which cards are hanging here is what it is asked for.** It was "whether any are", once, and the
    second card made in a session then arrived in a deck that had already been fetched: nothing could
    describe it, so it fell back to the row a card is deliberately not drawn as, in the middle of the
    grid the first one was drawn in. A joined list of the ids is the honest question — it changes when
    one is added and when one is taken away, and not otherwise.
  */
  const cardsHere = links.filter((one) => one.kind === 'FLASHCARD').map((one) => one.id)
  const wantedCards = cardsHere.join(',')
  const deck = useLoad(
    useCallback(
      async (signal: AbortSignal) => (wantedCards === '' ? null : await fetchCards(signal)),
      [wantedCards],
    ),
  )
  const cardsById = new Map<string, Flashcard>(
    (deck.load.status === 'ready' ? (deck.load.value ?? []) : []).map((card) => [card.id, card]),
  )

  /*
    The tasks themselves, for the same reason and by the same rule: an open point made out of a
    lecture is ticked off *here*, where it was written down, and a box needs to know whether it is
    ticked. The link says what a task is called and the list says how it stands.
  */
  const wantedTasks = links
    .filter((one) => one.kind === 'TASK')
    .map((one) => one.id)
    .join(',')
  const openPoints = useLoad(
    useCallback(
      async (signal: AbortSignal) => (wantedTasks === '' ? null : await fetchTasks(signal)),
      [wantedTasks],
    ),
  )
  const tasksById = new Map<string, Task>(
    (openPoints.load.status === 'ready' ? (openPoints.load.value ?? []) : []).map((task) => [
      task.id,
      task,
    ]),
  )

  /**
   * Ticks an open point off where it stands, or takes that back.
   *
   * The one thing in this list that *does* something rather than leading somewhere, and it is here
   * for the same reason it is in a search result: a session is read before the next lecture, and half
   * of what it says is "have I done that yet". Making somebody open the task list to answer would be
   * a step for its own sake — and the surface stays exactly as it is, so a card being written beside
   * it survives.
   */
  const tickOff = (one: Linked, done: boolean) => {
    attempt(async () => {
      await tickOffTask(one.id, done)
      openPoints.retry()
      announce(done ? 'Ticked off.' : 'Open again.')
    }, false)
  }

  /** Into the thing's own area, which is where it is edited and deleted. */
  const openIt = (one: Linked) => {
    const area = AREA_OF[one.kind]
    if (area !== undefined) {
      navigate(area, pathToOne(one))
    }
  }

  return (
    <section className="session__part">
      <div className="session__head">
        <h3 className="session__title">What belongs to it</h3>
      </div>

      {loading && <Waiting label="Looking up what belongs to this session." />}
      {failure !== null && <Failed failure={failure} onRetry={onRetry} title="That did not load" />}
      {problem !== null && <Failed failure={problem} />}

      {!loading && links.length === 0 && (
        <p className="session__aside">
          Nothing yet. Put the slides here, or make an open point out of what you did not follow —
          both stay ordinary things in your library and your list.
        </p>
      )}

      {grouped.map((group) => {
        /*
          Cards are drawn as cards — the same component the deck uses — and everything else as a row.
          A card's *shape* is the information: front and back side by side is what says which way
          round it is asked, and a line of grey text said none of it.

          A card the deck cannot describe — one deleted while this was open — is a row, and the two
          are kept apart rather than mixed: a row dropped into the grid of cards is a grey line
          standing where a card should be, which is what a card being made here used to look like.
        */
        const drawable =
          group.kind === 'FLASHCARD' ? group.rows.filter((one) => cardsById.has(one.id)) : []
        const asRows =
          group.kind === 'FLASHCARD'
            ? group.rows.filter((one) => !cardsById.has(one.id))
            : group.rows

        return (
          <div className="session__group" key={group.kind}>
            <h4 className="session__group-title">{group.title}</h4>
            {drawable.length > 0 && (
              <div className="belongs__cards">
                {drawable.map((one) => (
                  <div className="belongs__card" key={one.linkId}>
                    <CardSummary
                      card={cardsById.get(one.id)!}
                      onEdit={() => {
                        openIt(one)
                      }}
                    />
                    <Button
                      tone="bare"
                      disabled={working}
                      aria-label={`Unlink ${one.title} from this session`}
                      onClick={() => {
                        takeAway(one)
                      }}
                    >
                      Unlink
                    </Button>
                  </div>
                ))}
              </div>
            )}
            {asRows.length > 0 && (
              <ul className="belongs">
                {asRows.map((one) => (
                  <BelongsRow
                    key={one.linkId}
                    one={one}
                    task={tasksById.get(one.id) ?? null}
                    working={working}
                    onOpen={openIt}
                    onUnlink={takeAway}
                    onTickOff={(done) => {
                      tickOff(one, done)
                    }}
                  />
                ))}
              </ul>
            )}
          </div>
        )
      })}

      {/*
        Four ways to add something, and every one of them a visible control. Nothing here is reachable
        by a gesture alone, and nothing here opens a dialog: what each control needs is one field.
      */}
      <div className="controls controls--tight session__ways">
        <Button
          tone="quiet"
          disabled={working}
          onClick={() => {
            uploading.current?.click()
          }}
        >
          Upload a document
        </Button>
        <Button
          tone="quiet"
          disabled={working}
          onClick={() => {
            setAdding(adding === 'link' ? null : 'link')
          }}
        >
          Link something
        </Button>
        <Button
          tone="quiet"
          disabled={working}
          onClick={() => {
            setAdding(adding === 'task' ? null : 'task')
          }}
        >
          Make an open point
        </Button>
        <Button
          tone="quiet"
          disabled={working}
          onClick={() => {
            setAdding(adding === 'card' ? null : 'card')
          }}
        >
          Make a card
        </Button>
      </div>

      <input
        ref={uploading}
        className="session__file"
        type="file"
        multiple
        onChange={(event) => {
          upload(event.target.files)
          // Cleared, so that choosing the same file twice in a row is two uploads and not one.
          event.target.value = ''
        }}
      />

      {adding === 'link' && (
        <ContentPicker
          kinds={['document', 'note', 'task', 'flashcard']}
          label="Find something of yours to link to this session"
          placeholder="A document, a note, an open point, a card"
          lead="Everything you already have. Linking changes neither side — it only says that the two belong together."
          already={links.map((one) => one.id)}
          onChoose={(what) => {
            attempt(async () => {
              await make(
                what.kind.toUpperCase() as LinkedKind,
                what.id,
                null,
                `${what.label} belongs to this session.`,
              )
            })
          }}
          onClose={() => {
            setAdding(null)
          }}
        />
      )}

      {adding === 'task' && (
        <OneLine
          label="What is to be done"
          placeholder="§ 750 ZPO nochmal anschauen"
          action="Make it"
          busy={working}
          onDone={(text) => {
            attempt(async () => {
              const made = await addTask(text)
              await make(
                'TASK',
                made.id,
                'task',
                'That is on your list now, and hangs on this session.',
              )
            })
          }}
          onCancel={() => {
            setAdding(null)
          }}
        />
      )}

      {adding === 'card' && (
        <TwoSides
          busy={working}
          tags={cardTags}
          knownTags={knownTags}
          onTags={setCardTags}
          onDone={(draft) => {
            attempt(async () => {
              const made = await createCard({
                front: draft.front,
                back: draft.back,
                askedFor: draft.askedFor,
                reversible: draft.reversible,
                tags: [...draft.tags],
                frontFormat: draft.frontFormat,
                backFormat: draft.backFormat,
              })
              await make(
                'FLASHCARD',
                made.id,
                'flashcard',
                'That card is in your deck, and hangs on this session.',
              )
            })
          }}
          onCancel={() => {
            setAdding(null)
          }}
        />
      )}
    </section>
  )
}

/**
 * One line and one control — what an open point costs to make from here.
 *
 * Deliberately without a day: a task made out of a lecture is something to do, and MyLib's task list
 * puts undated ones first for exactly that reason. A date field here would be a field to skip.
 */
function OneLine({
  label,
  placeholder,
  action,
  busy,
  onDone,
  onCancel,
}: {
  readonly label: string
  readonly placeholder: string
  readonly action: string
  readonly busy: boolean
  readonly onDone: (text: string) => void
  readonly onCancel: () => void
}) {
  const [text, setText] = useState('')
  const ready = text.trim() !== ''

  return (
    <form
      className="session__make"
      onSubmit={(event) => {
        event.preventDefault()
        if (ready) {
          onDone(text.trim())
          setText('')
        }
      }}
    >
      <label className="field__label" htmlFor="session-one-line">
        {label}
      </label>
      <input
        id="session-one-line"
        className="field__control"
        autoFocus
        value={text}
        placeholder={placeholder}
        onChange={(event) => {
          setText(event.target.value)
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            onCancel()
          }
        }}
      />
      <div className="controls controls--tight">
        <Button tone="primary" type="submit" disabled={!ready || busy}>
          {action}
        </Button>
        <Button tone="bare" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
      </div>
    </form>
  )
}

/**
 * The two sides of a card, made where the question came up — and what it is filed under.
 *
 * It was the two sides and nothing else once, on the argument that somebody writing about a lecture
 * should not be stopped by a form. The argument was half right and the conclusion was wrong: what it
 * produced was a card with no tags, which is a card that no filter finds and no round of questions
 * reaches, so the "make it properly later" it counted on is a second visit that has to be remembered
 * and never is. A card made here is a card in the deck; leaving it unfilable does not save the work,
 * it defers it to a moment nobody schedules.
 *
 * So the cue and the tags stand here too, and the stopping is answered where it belongs — by them
 * being **optional and remembered**. Neither is needed to save, and both keep what was last typed
 * while the surface stays open, because a session's cards are a series: five cards off one lecture
 * carry the same two tags, and typing them five times is the thing that actually interrupts.
 *
 * **The direction switch is here too, like everything else.** It used to be left out on the grounds
 * that "asked backwards as well" is a statement about learning rather than about the lecture. That
 * was not a reason — it was this form having been written a second time and coming out thinner. The
 * card form is one form now (`views/cards/CardForm.tsx`), and what it can do it can do everywhere.
 */
function TwoSides({
  busy,
  tags,
  knownTags,
  onTags,
  onDone,
  onCancel,
}: {
  readonly busy: boolean
  /** Kept by the caller, so it survives this surface closing after every card. */
  readonly tags: readonly string[]
  readonly knownTags: readonly string[]
  readonly onTags: (tags: readonly string[]) => void
  readonly onDone: (draft: CardEntry) => void
  readonly onCancel: () => void
}) {
  const [front, setFront] = useState('')
  const [back, setBack] = useState('')
  const [frontFormat, setFrontFormat] = useState(MARKDOWN)
  const [backFormat, setBackFormat] = useState(MARKDOWN)
  const [askedFor, setAskedFor] = useState('')
  const [reversible, setReversible] = useState(false)
  const [typing, setTyping] = useState('')
  const [editorKey, setEditorKey] = useState(0)

  const draft: CardEntry = { front, back, frontFormat, backFormat, askedFor, tags, reversible }

  return (
    <div className="session__make">
      <CardForm
        draft={draft}
        onDraft={(next) => {
          setFront(next.front)
          setBack(next.back)
          setFrontFormat(next.frontFormat)
          setBackFormat(next.backFormat)
          setAskedFor(next.askedFor)
          setReversible(next.reversible)
          onTags(next.tags)
        }}
        knownTags={knownTags}
        tagText={typing}
        onTagText={setTyping}
        busy={busy}
        editorKey={editorKey}
        autoFocus
        submitLabel="Make the card"
        onSubmit={() => {
          if (draft.front.trim() === '' || draft.back.trim() === '' || busy) {
            return
          }
          onDone({ ...draft, front: front.trim(), back: back.trim(), askedFor: askedFor.trim() })
          setFront('')
          setBack('')
          // The cue, the tags, the markups and the direction stay: the next card off this lecture
          // wants the same ones.
          setEditorKey((one) => one + 1)
        }}
        onCancel={onCancel}
      />
    </div>
  )
}

/**
 * One thing that belongs to this session, and the two things that can be done with it.
 *
 * **Open is the answer to "why can I only unlink this?"** A link knows a kind and an id and nothing
 * else — that genericity is the whole of `core/link`, and it is what lets a card hang off a lecture
 * without either area learning the other exists. It also means this list is the wrong place to edit
 * or delete anything: correcting a task's wording or throwing a document away is that area's job,
 * with that area's rules, its undo and its warnings. So the row goes there instead of growing a
 * second, thinner copy of each.
 *
 * Unlink stays beside it and means what it says: the thing stays, it just no longer hangs here.
 *
 * **An open point is the one exception, and it is ticked off here.** Everything else on this list
 * leads somewhere; a task is answered by being done, and a session read before the next lecture is
 * half a list of "have I done that yet". The box is a sibling of the control that opens the row and
 * never a child of it — a checkbox inside a button is not a thing, and one control nested in another
 * is one neither the keyboard nor a screen reader can reach cleanly. Changing the wording or the day
 * still belongs to the task list, which is one press away.
 */
function BelongsRow({
  one,
  task,
  working,
  onOpen,
  onUnlink,
  onTickOff,
}: {
  readonly one: Linked
  /** The task this row points at, where the list knows it. `null` for everything else. */
  readonly task: Task | null
  readonly working: boolean
  readonly onOpen: (one: Linked) => void
  readonly onUnlink: (one: Linked) => void
  /** Ticks that task off, or takes it back. */
  readonly onTickOff: (done: boolean) => void
}) {
  const tickable = !one.gone && one.kind === 'TASK' && task !== null

  return (
    <li className={one.gone ? 'belongs__one belongs__one--gone' : 'belongs__one'}>
      {tickable && (
        <input
          type="checkbox"
          className="belongs__tick"
          disabled={working}
          checked={isDone(task)}
          aria-label={isDone(task) ? `Take back: ${task.text}` : `Tick off: ${task.text}`}
          onChange={(event) => {
            onTickOff(event.target.checked)
          }}
        />
      )}
      {one.gone ? (
        <span className="belongs__what">
          <span className="belongs__title">{one.title}</span>
          {one.detail !== '' && <span className="belongs__detail">{one.detail}</span>}
        </span>
      ) : (
        /* The thing itself is the control: pressing what you are looking at is the shortest way in. */
        <button
          type="button"
          className="belongs__what belongs__what--open"
          aria-label={`Open ${one.title}`}
          onClick={() => {
            onOpen(one)
          }}
        >
          <span className="belongs__title">{one.title}</span>
          {one.detail !== '' && <span className="belongs__detail">{one.detail}</span>}
        </button>
      )}
      <div className="controls controls--tight">
        {!one.gone && (
          <Button
            tone="bare"
            disabled={working}
            aria-label={`Open ${one.title} to change or delete it`}
            onClick={() => {
              onOpen(one)
            }}
          >
            Open
          </Button>
        )}
        <Button
          tone="bare"
          disabled={working}
          aria-label={`Unlink ${one.title} from this session`}
          onClick={() => {
            onUnlink(one)
          }}
        >
          {one.gone ? 'Clear' : 'Unlink'}
        </Button>
      </div>
    </li>
  )
}

/**
 * Which area each kind of linked content is edited and deleted in.
 *
 * A map and not a `when`, and it lives here rather than in `api/links` on purpose: which areas this
 * frontend has is the frontend's business, and the link itself must go on knowing nothing about
 * them. A kind with no area simply has no Open control.
 */
const AREA_OF: Readonly<Partial<Record<LinkedKind, string>>> = {
  DOCUMENT: 'library',
  NOTE: 'notes',
  TASK: 'tasks',
  FLASHCARD: 'cards',
  APPOINTMENT: 'timetable',
}

/**
 * The address inside that area, out of the id and nothing else.
 *
 * Which is all a link has, and the reason this is not simply `[one.id]` for every kind. The library
 * addresses a document by the place it lies in *and* its id, so an id on its own would read as a
 * folder — one nobody ever made, drawn as an empty shelf; the plan addresses an appointment by the
 * week it is on and its id, so an id on its own would read as a view and show this week with nothing
 * open. Both areas take "and I do not know where" and correct the address once they have asked. The
 * cards area names a tab in the first step of its address, so a card is asked for under `card/`.
 */
function pathToOne(one: Linked): string[] {
  switch (one.kind) {
    case 'DOCUMENT':
      return pathToDocumentWhereverItLies(one.id)
    case 'APPOINTMENT':
      return pathToAppointmentWheneverItIs(one.id)
    // The cards area addresses one card under `card/`: the first step of its address is a tab, so an
    // id on its own read as none of them and opened the writing surface with nothing in it.
    case 'FLASHCARD':
      return ['card', one.id]
    default:
      return [one.id]
  }
}

/** Which of the four ways to add something is open, or none. */
type Adding = 'link' | 'task' | 'card' | null

/**
 * How what belongs to a session is grouped, and in which order.
 *
 * Material first, because that is what somebody opens a past session for; what is still to be done
 * after it, because that is what a review is; and the cards last, because they are reached by
 * learning rather than by reading.
 */
const GROUPS: readonly { readonly kind: LinkedKind; readonly title: string }[] = [
  { kind: 'DOCUMENT', title: 'Material' },
  { kind: 'NOTE', title: 'Notes' },
  { kind: 'TASK', title: 'Still to do' },
  { kind: 'FLASHCARD', title: 'Cards' },
  { kind: 'APPOINTMENT', title: 'Other sessions' },
]
